import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import {
  PushNotifications,
  type Token,
  type PushNotificationSchema,
  type ActionPerformed,
} from '@capacitor/push-notifications';
import {
  AndroidSettings,
  IOSSettings,
  NativeSettings,
} from 'capacitor-native-settings';
import toast from 'react-hot-toast';
import { supabase } from '../supabase/client';
import { UserPreferencesService } from '../api/userPreferencesService';
import { appNavigate } from '../../utils/appNavigate';
import { getNotificationOpenPath } from '../../utils/userNotificationPolicy';

export const PUSH_PERMISSION_EVENT = 'fixnow:push-permission';

export type NativePushPermission = 'granted' | 'denied' | 'prompt' | 'unavailable';

let listenersAttached = false;
let activeUserId: string | null = null;
let resumeListenerAttached = false;
/** 使用者從「前往設定」返回 App 時，自動再試註冊推播 */
let pushSettingsRetryUserId: string | null = null;

function emitPushPermission(granted: boolean): void {
  window.dispatchEvent(
    new CustomEvent(PUSH_PERMISSION_EVENT, { detail: { granted } })
  );
}

export async function getNativePushPermission(): Promise<NativePushPermission> {
  if (!Capacitor.isNativePlatform()) return 'unavailable';
  try {
    const perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'granted') return 'granted';
    if (perm.receive === 'denied') return 'denied';
    return 'prompt';
  } catch {
    return 'unavailable';
  }
}

async function upsertDeviceToken(userId: string, token: string): Promise<void> {
  const platform = Capacitor.getPlatform();
  const platformKey =
    platform === 'ios' ? 'ios' : platform === 'android' ? 'android' : 'web';

  const { error } = await supabase.from('device_push_tokens').upsert(
    {
      user_id: userId,
      token,
      platform: platformKey,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,token' }
  );

  if (error) {
    console.warn('[NativePush] 儲存 token 失敗:', error.message);
  }
}

function attachListeners(userId: string): void {
  if (listenersAttached) return;
  listenersAttached = true;

  PushNotifications.addListener('registration', (t: Token) => {
    if (!t.value || !activeUserId) return;
    void upsertDeviceToken(activeUserId, t.value);
  });

  PushNotifications.addListener('registrationError', (err) => {
    console.warn('[NativePush] registrationError:', err);
  });

  PushNotifications.addListener('pushNotificationReceived', (n: PushNotificationSchema) => {
    // 前景改由 Realtime 站內橫幅提示，避免與 FCM / 鈴鐺重複
    console.log('[NativePush] received (foreground):', n.title);
  });

  PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
    const data = action.notification.data as { type?: string } | undefined;
    appNavigate(getNotificationOpenPath(data?.type ?? ''));
  });
}

function ensurePushSettingsResumeListener(): void {
  if (resumeListenerAttached || !Capacitor.isNativePlatform()) return;
  resumeListenerAttached = true;

  App.addListener('appStateChange', ({ isActive }) => {
    if (!isActive || !pushSettingsRetryUserId) return;

    const userId = pushSettingsRetryUserId;
    void (async () => {
      try {
        const prefs = await UserPreferencesService.getUserPreferences(userId);
        if (prefs?.push_notifications === false) {
          pushSettingsRetryUserId = null;
          return;
        }
      } catch {
        /* 仍嘗試註冊 */
      }

      const ok = await registerNativePush(userId);
      if (ok) {
        pushSettingsRetryUserId = null;
        try {
          await UserPreferencesService.createOrUpdatePreferences(userId, {
            push_notifications: true,
          });
        } catch {
          /* 權限已開，偏好稍後可再存 */
        }
        emitPushPermission(true);
        toast.success('通知權限已開啟，推播已啟用');
      }
    })();
  });
}

/** 開啟系統「本 App 通知」設定（Android / iOS） */
export async function openAppNotificationSettings(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;

  try {
    const { status } = await NativeSettings.open({
      optionAndroid: AndroidSettings.AppNotification,
      optionIOS: IOSSettings.AppNotification,
    });
    return status;
  } catch (e) {
    console.warn('[NativePush] openAppNotificationSettings:', e);
    try {
      const { status } = await NativeSettings.open({
        optionAndroid: AndroidSettings.ApplicationDetails,
        optionIOS: IOSSettings.App,
      });
      return status;
    } catch (e2) {
      console.warn('[NativePush] openAppNotificationSettings fallback:', e2);
      return false;
    }
  }
}

/** 從設定返回 App 時自動再試 `registerNativePush` */
export function schedulePushPermissionRetryAfterSettings(userId: string): void {
  pushSettingsRetryUserId = userId;
  ensurePushSettingsResumeListener();
}

/** 請求權限並向 FCM 註冊（僅原生 App） */
export async function registerNativePush(userId: string): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;

  activeUserId = userId;
  attachListeners(userId);

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === 'prompt') {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== 'granted') {
    return false;
  }

  await PushNotifications.register();
  return true;
}

/** 登入且偏好允許推播時自動註冊 */
export async function syncNativePushRegistration(userId: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const prefs = await UserPreferencesService.getUserPreferences(userId);
    if (prefs?.push_notifications === false) return;
    await registerNativePush(userId);
  } catch (e) {
    console.warn('[NativePush] sync skipped:', e);
  }
}

export async function unregisterNativePushTokens(userId: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (pushSettingsRetryUserId === userId) {
    pushSettingsRetryUserId = null;
  }
  const { error } = await supabase.from('device_push_tokens').delete().eq('user_id', userId);
  if (error) {
    console.warn('[NativePush] 清除 token 失敗:', error.message);
  }
}
